<template>
    <div class="anexo-card">
        <div class="anexo-card-header">
            <div class="anexo-card-icon">
                {{ iconeArquivo }}
            </div>

            <div class="anexo-card-title-area">
                <div class="anexo-card-title" :title="anexo.nmdoc || 'Anexo'">
                    {{ anexo.nmdoc || 'Anexo' }}
                </div>

                <div class="anexo-card-subtitle">
                    {{ anexo.tiponm || 'Arquivo' }}
                </div>
            </div>

            <span
                v-if="anexo.situacaonm"
                class="anexo-card-badge"
                :class="classeSituacao"
            >
                {{ anexo.situacaonm }}
            </span>
        </div>

        <div class="anexo-card-body">
            <div v-if="anexo.nmanexogrupo" class="anexo-card-info-full">
                <span class="label">Grupo</span>
                <span class="value">{{ anexo.nmanexogrupo }}</span>
            </div>

            <div v-if="anexo.nmtpdoc" class="anexo-card-info-full">
                <span class="label">Documento</span>
                <span class="value">{{ anexo.nmtpdoc }}</span>
            </div>

            <div class="anexo-card-info-grid">
                <div v-if="anexo.dtanexo" class="anexo-card-info">
                    <span class="label">Data</span>
                    <span class="value">{{ formatDate(anexo.dtanexo) }}</span>
                </div>

                <div class="anexo-card-info">
                    <span class="label">Validade</span>
                    <span class="value">{{ anexo.dtvalidade ? formatDate(anexo.dtvalidade) : '—' }}</span>
                </div>

                <div v-if="anexo.stvencimento" class="anexo-card-info">
                    <span class="label">Prazo</span>
                    <span class="value" :class="classePrazo">{{ anexo.stvencimento }}</span>
                </div>

                <div v-if="anexo.nmep" class="anexo-card-info">
                    <span class="label">EP</span>
                    <span class="value">{{ anexo.nmep }}</span>
                </div>
            </div>

            <div v-if="anexo.nmsim || anexo.nmestabelecimento" class="anexo-card-extra">
                <div v-if="anexo.nmsim">
                    <strong>SIM:</strong> {{ anexo.nmsim }}
                </div>

                <div v-if="anexo.nmestabelecimento">
                    <strong>Estab.:</strong> {{ anexo.nmestabelecimento }}
                </div>
            </div>
        </div>

        <div class="anexo-card-actions">
            <button
                type="button"
                class="btn-card-action"
                @click="lerAnexo"
            >
                Download
            </button>

            <button
                v-if="permiteRemover"
                type="button"
                class="btn-card-action danger"
                @click="$emit('remover', anexo)"
            >
                Remover
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Api } from '@/funcoes/apiExport';

const props = defineProps({
    anexo: {
        type: Object,
        required: true,
    },
    permiteRemover: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['remover']);

const isLoading = ref(false);

const iconeArquivo = computed(() => {
    const tipo = String(props.anexo?.tipo ?? '');
    const tiponm = String(props.anexo?.tiponm ?? '').toLowerCase();

    if (tiponm.includes('pdf')) return '📄';
    if (tiponm.includes('imagem') || tiponm.includes('jpg') || tiponm.includes('png') || tiponm.includes('foto')) return '🖼️';
    if (tiponm.includes('vídeo') || tiponm.includes('video')) return '🎥';

    switch (tipo) {
        case '0': return '📄';
        case '1': return '🖼️';
        case '2': return '🎥';
        default: return '📎';
    }
});

const classeSituacao = computed(() => {
    switch (Number(props.anexo?.situacao)) {
        case 9: return 'ok';
        case 7: return 'info';
        case 5: return 'warning';
        case 3: return 'danger';
        default: return 'neutral';
    }
});

const classePrazo = computed(() => {
    const prazo = String(props.anexo?.stvencimento ?? '').toUpperCase();

    if (prazo.includes('VENCIDO')) return 'prazo-danger';
    if (prazo.includes('VENCE')) return 'prazo-warning';
    if (prazo.includes('PRAZO')) return 'prazo-ok';

    return '';
});

function formatDate(value) {
    if (!value) return '—';

    const [datePart] = String(value).split('T');
    const parts = datePart.split('-');

    if (parts.length !== 3) return value;

    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
}


async function lerAnexo() {
    try {
        isLoading.value = true;
        const r = await Api.genericPost('/operacoes/solicitar/download', {
            id: props.anexo.id
        });
        if (r.bolok === 1) {
            window.open(r.download_url, '_blank');
        } else {
            console.log('não foi possível ler o anexo...');
            // msgAlerta.value = `ATENÇÃO: ${r.erro || 'Não foi possível abrir o anexo.'}`;
        }
    } catch (error) {
        console.log(error);
        // msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}
</script>

<style scoped>
.anexo-card {
    display: flex;
    flex-direction: column;
    min-height: 210px;
    background: #fff;
    border: 1px solid #e3e3e3;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .06);
    overflow: hidden;
}

.anexo-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #eee;
    background: #fafafa;
}

.anexo-card-icon {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: #f0f0f0;
    font-size: 18px;
    flex-shrink: 0;
}

.anexo-card-title-area {
    min-width: 0;
    flex: 1;
}

.anexo-card-title {
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.anexo-card-subtitle {
    margin-top: 2px;
    font-size: 12px;
    color: #777;
}

.anexo-card-badge {
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.anexo-card-badge.ok {
    background: #e8f5e9;
    color: #2e7d32;
}

.anexo-card-badge.info {
    background: #e3f2fd;
    color: #1565c0;
}

.anexo-card-badge.warning {
    background: #fff3e0;
    color: #ef6c00;
}

.anexo-card-badge.danger {
    background: #ffebee;
    color: #c62828;
}

.anexo-card-badge.neutral {
    background: #eeeeee;
    color: #555;
}

.anexo-card-body {
    flex: 1;
    padding: 12px 14px;
}

.anexo-card-info-full {
    margin-bottom: 8px;
}

.anexo-card-info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 14px;
    margin-top: 8px;
}

.anexo-card-info .label,
.anexo-card-info-full .label {
    display: block;
    font-size: 11px;
    color: #777;
    margin-bottom: 2px;
}

.anexo-card-info .value,
.anexo-card-info-full .value {
    font-size: 13px;
    color: #333;
    word-break: break-word;
}

.anexo-card-extra {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dashed #ddd;
    font-size: 12px;
    color: #555;
}

.prazo-ok {
    color: #2e7d32 !important;
    font-weight: 600;
}

.prazo-warning {
    color: #ef6c00 !important;
    font-weight: 600;
}

.prazo-danger {
    color: #c62828 !important;
    font-weight: 600;
}

.anexo-card-actions {
    display: flex;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #eee;
    background: #fafafa;
}

.btn-card-action {
    border: 1px solid #d5d5d5;
    background: #fff;
    color: #333;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    cursor: pointer;
}

.btn-card-action:hover {
    background: #f1f1f1;
}

.btn-card-action.danger {
    color: #c62828;
    border-color: #efb5b5;
}

.btn-card-action.danger:hover {
    background: #ffebee;
}
</style>
